import type { AutocompleteOption } from './AddUserDialog';
import type { IChangeEvent } from '@rjsf/core';
import type { RegistryWidgetsType, RJSFSchema } from '@rjsf/utils';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { normalizeLocale, resolveConnectionInfoSchemaI18n } from '@utils/JsonSchemaUtil';
import { Autocomplete, TextField } from '@mui/material';
import validator from '@rjsf/validator-ajv8';
import debounce from 'lodash.debounce';
import { SecureSubmitButton } from '@components/buttons/SecureSubmitButton';
import { Button, Dialog } from '@components/index';
import {
  CustomSelectWidget,
  CustomTextWidget,
} from '@pages/customer/manager/workflow/connectionMgmt/detail/appLogin/AppLogin';
import { useStore } from '@stores/RootStore';
import { useAddRemoteUser } from '@queryHooks/useSubscription';
import { useGetSubscriptionConnection } from '@queryHooks/useSubscriptionConnection';
import { useGetTenantMembersInfinite } from '@queryHooks/useTenant';
import { DomUtil } from '@utils/DomUtil';
import { TenantMemberSimpleModel } from '@repositories/tenantMemberRepository/models/TenantMemberSimpleModel';
import * as S from './LinkUserDialogStyles';

type Props = {
  subscriptionId: string;
  open: boolean;
  onClose: () => void;
};

const AddUserFromSchemaDialog = ({ subscriptionId, open, onClose }: Props) => {
  const { t, i18n } = useTranslation();
  const {
    authStore,
    uiStore: { toastStore },
  } = useStore();
  const [user, setUser] = useState<TenantMemberSimpleModel>(new TenantMemberSimpleModel());
  const [memberFilter, setMemberFilter] = useState<{ size: number; keyword?: string }>({
    size: 100,
    keyword: undefined,
  });

  const { data: subscriptionConnection } = useGetSubscriptionConnection(subscriptionId, {
    enabled: open,
    staleTime: 0,
    cacheTime: 0,
  });

  const {
    isSuccess: isUserListSuccess,
    data: userListPage,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
  } = useGetTenantMembersInfinite(authStore.managerTenantInfo.id, memberFilter, { enabled: open });

  const handleMemberSearchChange = debounce((e: React.ChangeEvent<HTMLInputElement>) => {
    setMemberFilter(prev => ({ ...prev, keyword: e.target.value }));
  }, 1000);

  const options: AutocompleteOption[] = isUserListSuccess
    ? userListPage.pages.reduce(
        (accum, cur) => [...accum, ...cur.content.map(({ id, name, email }) => ({ id, name, email }))],
        [] as AutocompleteOption[],
      )
    : [];

  const handleAutocompleteScroll = debounce((event: React.UIEvent<HTMLUListElement>) => {
    if (DomUtil.isScrollBottom(event.target as HTMLUListElement) && !isFetchingNextPage && hasNextPage) {
      fetchNextPage();
    }
  }, 100);

  const widgets: RegistryWidgetsType = {
    TextWidget: CustomTextWidget,
    SelectWidget: CustomSelectWidget,
  };

  const { mutate: addRemoteUser, isPending } = useAddRemoteUser();

  const handleSubmit = (data: IChangeEvent<any, RJSFSchema, any>) => {
    addRemoteUser(
      { subscriptionId: subscriptionId as string, raData: { userId: user.id, userInfo: data.formData } },
      {
        onSuccess: () => {
          toastStore.open(t('Add_User_Success'));
          setUser(new TenantMemberSimpleModel());
          onClose();
        },
        onError: () => {
          toastStore.open(t('Error.Unknown_Error'));
        },
      },
    );
  };

  const handleClose = () => {
    setUser(new TenantMemberSimpleModel());
    onClose();
  };

  const addUserFormSchema = useMemo(
    () =>
      resolveConnectionInfoSchemaI18n(
        subscriptionConnection?.connection?.connector?.addUserSchema ??
          subscriptionConnection?.addUserSchemaObject ??
          {},
        normalizeLocale(i18n.language),
      ),
    [subscriptionConnection, i18n.language],
  );

  return (
    <Dialog open={open} onClose={handleClose} title={t('Subscrib_Detail_User_18')} height={600}>
      <S.SubscriptionUserLabel>{t('Member_Group_07')}</S.SubscriptionUserLabel>
      <Autocomplete
        onChange={(event, value) => {
          setUser(new TenantMemberSimpleModel(value as AutocompleteOption));
        }}
        options={options}
        value={user}
        getOptionLabel={option => {
          return option.name === '-' ? '' : `${option.name} (${option.email})`;
        }}
        renderInput={params => (
          <TextField
            {...params}
            placeholder={t('Subscrib_Detail_User_23')}
            variant="outlined"
            onChange={handleMemberSearchChange}
          />
        )}
        ListboxProps={{ onScroll: handleAutocompleteScroll, role: 'list-box' }}
        sx={{ marginBottom: '20px' }}
      />

      <S.StyledForm
        schema={addUserFormSchema}
        validator={validator}
        onSubmit={handleSubmit}
        uiSchema={{
          'ui:globalOptions': {
            label: false,
          },
          'ui:submitButtonOptions': {
            norender: true,
          },
        }}
        widgets={widgets}
      >
        <Dialog.Footer>
          <SecureSubmitButton isPending={isPending} variant="contain" type="submit" size="extraLarge">
            {t('Member_User_24')}
          </SecureSubmitButton>
          <Button variant="outline" size="extraLarge" onClick={handleClose}>
            {t('Subscrib_Detail_BuyRequest_13')}
          </Button>
        </Dialog.Footer>
      </S.StyledForm>
    </Dialog>
  );
};

export default AddUserFromSchemaDialog;
